import type { BadgeProps } from '@reelplicate/ui';

import type {
  Asset,
  AssetKind,
  AssetSource,
  AssetStatus,
  Collection,
  FilterState,
  Owner,
  SearchSignal,
  SmartRule,
} from './types';

export const KIND_LABELS: Record<AssetKind, { one: string; many: string }> = {
  reel: { one: 'Reel', many: 'Reels' },
  carousel: { one: 'Carousel', many: 'Carousels' },
  clip: { one: 'Clip', many: 'Clips & b-roll' },
  image: { one: 'Image', many: 'Images' },
  audio: { one: 'Audio', many: 'Audio' },
  caption: { one: 'Caption', many: 'Captions' },
};

export const SOURCE_LABELS: Record<AssetSource, string> = {
  generated: 'Generated',
  uploaded: 'Uploaded',
  saved: 'Saved from competitors',
};

export const STATUS: Record<
  AssetStatus,
  { label: string; dot: string; tone: NonNullable<BadgeProps['tone']> }
> = {
  published: { label: 'Published', dot: 'bg-status-success-solid', tone: 'success' },
  scheduled: { label: 'Scheduled', dot: 'bg-brand-strong', tone: 'brand' },
  review: { label: 'In review', dot: 'bg-status-warning-solid', tone: 'warning' },
  draft: { label: 'Draft', dot: 'bg-border-strong', tone: 'neutral' },
  saved: { label: 'Saved', dot: 'bg-status-info-solid', tone: 'info' },
};

/** "Reel", "Carousel · 7" */
export function typeLabel(asset: Asset): string {
  const one = KIND_LABELS[asset.kind].one;
  return asset.slides ? `${one} · ${asset.slides}` : one;
}

/** "0:24", "1:42" */
export function formatDuration(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** "today", "2d", "1w" */
export function formatAgo(days: number): string {
  if (days === 0) return 'today';
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
export const formatViews = (views: number) => compact.format(views);

/** "Generated · Oat & Ember · today", "Saved from Oatly · 4d", "Uploaded · 5d" */
export function assetMeta(asset: Asset): string {
  const origin =
    asset.source === 'saved'
      ? `Saved from ${asset.savedFrom}`
      : SOURCE_LABELS[asset.source];
  const account = asset.source === 'generated' && asset.account ? [asset.account] : [];
  return [origin, ...account, formatAgo(asset.addedDaysAgo)].join(' · ');
}

/** Characters of a caption against the platform limit, "142 / 2,200". */
export function captionCount(caption: string): string {
  return `${[...caption].length} / 2,200`;
}

/**
 * A hand-picked collection's assets from the loaded library. A smart
 * collection's matches come from the server (`useCollectionEntries`), so
 * this is empty for one.
 */
export const collectionItems = (collection: Collection, assets: Asset[]) =>
  collection.smart ? [] : assets.filter((a) => collection.assetIds?.includes(a.id));

const normaliseText = (text = '') => text.replace(/\s+/g, ' ').trim();

/**
 * Captions saved from an asset's slides, by slide number. Captions saved
 * before they kept their source are matched on their text.
 */
export function slideCaptions(asset: Asset, assets: Asset[]): Map<number, Asset[]> {
  const byText = new Map(
    (asset.slideText ?? []).flatMap((s) =>
      s.text ? [[normaliseText(s.text), s.slide] as const] : [],
    ),
  );
  const saved = new Map<number, Asset[]>();
  for (const caption of assets) {
    if (caption.kind !== 'caption') continue;
    const slide = caption.sourceAssetId
      ? caption.sourceAssetId === asset.id
        ? caption.sourceSlide
        : undefined
      : byText.get(normaliseText(caption.caption));
    if (slide === undefined) continue;
    saved.set(slide, [...(saved.get(slide) ?? []), caption]);
  }
  return saved;
}

/** What's been saved from each scene of a reel: its captions and its clips. */
export function sceneSaves(
  asset: Asset,
  assets: Asset[],
): Map<number, { captions: Asset[]; clips: Asset[] }> {
  const saved = new Map<number, { captions: Asset[]; clips: Asset[] }>();
  for (const item of assets) {
    if (item.sourceAssetId !== asset.id || item.sourceScene === undefined) continue;
    const entry = saved.get(item.sourceScene) ?? { captions: [], clips: [] };
    if (item.kind === 'caption') entry.captions.push(item);
    // Replicas are shown on their own (useSceneReplicas), not as saved clips.
    else if (item.kind === 'clip' && !item.tags.includes('scene-replica'))
      entry.clips.push(item);
    saved.set(item.sourceScene, entry);
  }
  return saved;
}

/** The hand-picked collections holding any of `items`. */
export const collectionsHolding = (collections: Collection[], items: Asset[]) =>
  collections.filter(
    (c) => !c.smart && items.some((item) => c.assetIds?.includes(item.id)),
  );

/** A collection's covers (its first assets), from the loaded library. */
export const collectionCovers = (collection: Collection, assets: Asset[]) =>
  collection.coverIds.flatMap((id) => assets.find((a) => a.id === id) ?? []);

/* ---------------------------------------------------------------- filters */

type Option = { value: string; label: string; matches: (asset: Asset) => boolean };

export type Facet = {
  id: string;
  label: string;
  /** Several options (OR), or one. */
  multiple: boolean;
  options: Option[];
};

const within = (lo: number, hi: number) => (a: Asset) =>
  a.duration !== undefined && a.duration >= lo && a.duration < hi;

/**
 * The library's filters. Account lists the workspace's owners: the brand,
 * then each persona.
 */
export const makeFacets = (owners: Owner[]): Facet[] => [
  {
    id: 'type',
    label: 'Type',
    multiple: true,
    options: (Object.keys(KIND_LABELS) as AssetKind[]).map((kind) => ({
      value: kind,
      label: KIND_LABELS[kind].many,
      matches: (a) => a.kind === kind,
    })),
  },
  {
    id: 'source',
    label: 'Source',
    multiple: true,
    options: [
      ...(Object.keys(SOURCE_LABELS) as AssetSource[]).map((source) => ({
        value: source,
        label: SOURCE_LABELS[source],
        matches: (a: Asset) => a.source === source,
      })),
      {
        value: 'published',
        label: 'Published',
        matches: (a) => a.status === 'published',
      },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    multiple: true,
    options: (Object.keys(STATUS) as AssetStatus[]).map((status) => ({
      value: status,
      label: STATUS[status].label,
      matches: (a) => a.status === status,
    })),
  },
  {
    id: 'platform',
    label: 'Platform',
    multiple: true,
    options: [
      { value: 'tiktok', label: 'TikTok' },
      { value: 'instagram', label: 'Instagram' },
      { value: 'youtube', label: 'YouTube' },
    ].map((o) => ({
      ...o,
      matches: (a: Asset) => a.platforms?.includes(o.value as never) ?? false,
    })),
  },
  {
    id: 'account',
    label: 'Account',
    multiple: true,
    options: owners.map((owner) => ({
      value: owner.id,
      label: owner.name,
      matches: (a) => a.ownerId === owner.id,
    })),
  },
  {
    id: 'duration',
    label: 'Duration',
    multiple: true,
    options: [
      { value: 'short', label: 'Under 15s', matches: within(0, 15) },
      { value: 'mid', label: '15–30s', matches: within(15, 31) },
      { value: 'long', label: 'Over 30s', matches: within(31, Infinity) },
    ],
  },
  {
    id: 'performance',
    label: 'Performance',
    multiple: false,
    options: [10_000, 50_000, 100_000].map((min) => ({
      value: String(min),
      label: `≥ ${formatViews(min)} views`,
      matches: (a) => (a.views ?? 0) >= min,
    })),
  },
  {
    id: 'added',
    label: 'Date added',
    multiple: false,
    options: [
      { value: '1', label: 'Today', max: 0 },
      { value: '7', label: 'Last 7 days', max: 6 },
      { value: '30', label: 'Last 30 days', max: 29 },
    ].map(({ max, ...o }) => ({ ...o, matches: (a: Asset) => a.addedDaysAgo <= max })),
  },
];

/**
 * Everything a smart collection's rule can use: the library's filters, plus
 * tags and competitor signals. The signals read a saved reel's competitor
 * post, which only the server has, so they never match in the browser - they
 * are here for the rule builder and for describing a rule.
 */
export const makeRuleFacets = (
  owners: Owner[],
  competitors: { id: string; label: string }[],
  tags: string[],
): Facet[] => {
  const never = () => false;
  const days = (labels: [string, string][]) =>
    labels.map(([value, label]) => ({ value, label, matches: never }));
  return [
    ...makeFacets(owners),
    {
      id: 'tags',
      label: 'Tag',
      multiple: true,
      options: tags.map((tag) => ({
        value: tag,
        label: tag,
        matches: (a: Asset) => a.tags.includes(tag),
      })),
    },
    {
      id: 'competitor',
      label: 'Competitor',
      multiple: true,
      options: competitors.map((c) => ({ value: c.id, label: c.label, matches: never })),
    },
    {
      id: 'lift',
      label: 'Beats their usual views',
      multiple: false,
      options: days([
        ['2', '2× or more'],
        ['3', '3× or more'],
        ['5', '5× or more'],
      ]),
    },
    {
      id: 'winner',
      label: 'Competitor winner',
      multiple: false,
      options: days([
        ['7', 'In the last 7 days'],
        ['30', 'In the last 30 days'],
        ['any', 'Ever'],
      ]),
    },
    {
      id: 'posted',
      label: 'Posted',
      multiple: false,
      options: days([
        ['1', 'Today'],
        ['7', 'In the last 7 days'],
        ['30', 'In the last 30 days'],
      ]),
    },
  ];
};

export type { FilterState };

export function applyFilters(
  assets: Asset[],
  filters: FilterState,
  facets: Facet[],
): Asset[] {
  return assets.filter((asset) =>
    facets.every((facet) => {
      const picked = filters[facet.id] ?? [];
      return (
        picked.length === 0 ||
        facet.options.some((o) => picked.includes(o.value) && o.matches(asset))
      );
    }),
  );
}

/** "Type", "Type: Reels", "Type: Reels +2" */
export function facetChipLabel(facet: Facet, picked: string[]): string {
  if (picked.length === 0) return facet.label;
  const first = facet.options.find((o) => o.value === picked[0])?.label ?? '';
  return `${facet.label}: ${first}${picked.length > 1 ? ` +${picked.length - 1}` : ''}`;
}

/* ----------------------------------------------------------------- search */

export type SearchHit = { asset: Asset; reason?: SearchSignal; score: number };

const tokens = (query: string) =>
  query
    .toLowerCase()
    .split(/[\s,]+/)
    .filter((t) => t.length > 1);

/**
 * Text search: an asset matches when a query word is in one of its tags
 * (ranked first) or its title, caption or transcript. Every hit says why it
 * matched.
 */
export function search(assets: Asset[], query: string): SearchHit[] {
  const words = tokens(query);
  if (words.length === 0) return assets.map((asset) => ({ asset, score: 0 }));
  const hits: SearchHit[] = [];
  for (const asset of assets) {
    const tag = asset.tags.find((t) => words.some((w) => t.toLowerCase().includes(w)));
    if (tag) {
      hits.push({
        asset,
        reason: { kind: 'tag', label: `Tag: ${tag}`, terms: [] },
        score: 5,
      });
      continue;
    }
    const text =
      `${asset.title} ${asset.caption ?? ''} ${asset.transcript ?? ''}`.toLowerCase();
    const word = words.find((w) => text.includes(w));
    if (word) {
      hits.push({
        asset,
        reason: { kind: 'text', label: `Text: “${word}”`, terms: [] },
        score: 1,
      });
    }
  }
  return hits.sort((a, b) => b.score - a.score);
}

/* ------------------------------------------------------------ smart rules */

/** How a rule reads: "≥ 50K views", "status = In review · matches “latte”". */
export function describeRule(rule: SmartRule, facets: Facet[]): string {
  const parts = facets.flatMap((facet) => {
    const labels = (rule.filters[facet.id] ?? []).flatMap(
      (value) => facet.options.find((o) => o.value === value)?.label ?? [],
    );
    if (labels.length === 0) return [];
    return facet.id === 'performance' || facet.id === 'added'
      ? labels
      : [`${facet.label.toLowerCase()} = ${labels.join(' or ')}`];
  });
  const query = rule.query?.trim();
  if (query) parts.push(`matches “${query}”`);
  if (rule.includeCompetitorFeed) parts.push('with competitors’ unsaved posts');
  return parts.join(' · ') || 'matches everything';
}

/* ------------------------------------------------------------------- sort */

export const SORTS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'views', label: 'Most viewed' },
  { value: 'name', label: 'Name' },
] as const;
export type SortId = (typeof SORTS)[number]['value'];

export function sortHits(hits: SearchHit[], sort: SortId): SearchHit[] {
  if (sort === 'relevance') return hits;
  const by: Record<Exclude<SortId, 'relevance'>, (a: Asset, b: Asset) => number> = {
    newest: (a, b) => a.addedDaysAgo - b.addedDaysAgo,
    oldest: (a, b) => b.addedDaysAgo - a.addedDaysAgo,
    views: (a, b) => (b.views ?? -1) - (a.views ?? -1),
    name: (a, b) => a.title.localeCompare(b.title),
  };
  return [...hits].sort((a, b) => by[sort](a.asset, b.asset));
}
